/**
 * Authentication API - Pure JWT / Stateless
 * Maps to Authentication.Service/Controllers/AuthController.cs via Gateway
 * 
 * Pure JWT Pattern:
 * - Tokens stored in localStorage (no httpOnly cookies)
 * - Authorization header automatically attached by client interceptor
 * - Frontend manages token refresh explicitly
 */

import apiClient from './client';
import type { LoginRequest, RegisterRequest, LoginResponse, User } from '../types';

export interface ResendVerificationRequest {
  sessionToken: string;
}

// Token storage utilities

// The backend serializes ClaimTypes.Role with the default outbound map, so the
// payload key is the long URI; short "role" kept as a fallback (matches the
// gateway's JwtClaimToHeaderMiddleware).
const ROLE_CLAIM_KEYS = ['role', 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    // JWT payloads are base64url; convert + pad before atob.
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
};

/**
 * Keep the cached `auth_user` in lockstep with the live access token. The
 * stored user is a login-time snapshot, but refresh / switch-church mint new
 * tokens whose role + churchId claims can differ — without this sync,
 * role-gated UI (like the admin sidebar) flaps depending on which stale
 * snapshot the app rehydrated. Dispatches 'auth-user-synced' so AuthContext
 * can update React state in the same tab.
 */
export const syncStoredUserWithToken = (accessToken: string): void => {
  try {
    const stored = localStorage.getItem('auth_user');
    if (!stored) return;
    const payload = decodeJwtPayload(accessToken);
    if (!payload) return;

    const role = ROLE_CLAIM_KEYS.map((k) => payload[k]).find((v): v is string => typeof v === 'string');
    const churchId = typeof payload.churchId === 'string' ? payload.churchId : undefined;

    const user = JSON.parse(stored);
    let changed = false;
    if (role && user.userRole !== role) {
      user.userRole = role;
      changed = true;
    }
    if (churchId && churchId !== EMPTY_GUID && user.churchId !== churchId) {
      user.churchId = churchId;
      changed = true;
    }
    if (changed) {
      localStorage.setItem('auth_user', JSON.stringify(user));
      window.dispatchEvent(new Event('auth-user-synced'));
    }
  } catch {
    // A sync failure must never break auth itself.
  }
};

export const saveTokens = (accessToken: string, refreshToken: string): void => {
  localStorage.setItem('access_token', accessToken);
  localStorage.setItem('refresh_token', refreshToken);
  // Every token mint (login, refresh, switch-church) re-syncs the cached user.
  syncStoredUserWithToken(accessToken);
};

export const getAccessToken = (): string | null => {
  return localStorage.getItem('access_token');
};

export const getRefreshToken = (): string | null => {
  return localStorage.getItem('refresh_token');
};

export const clearTokens = (): void => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('auth_user');
};

export const authApi = {
  /**
   * Login with Google ID Token (Client-Side OAuth)
   * POST /users/login/google (matches gateway auth-direct-users-route)
   */
  loginWithGoogle: async (idToken: string, context?: string): Promise<LoginResponse> => {
    const response = await apiClient.post('/users/login/google', {
      idToken,
      context: context || 'admin_portal'
    });
    
    // Save tokens from response
    const responseData = response.data.data || response.data;
    if (responseData.token && responseData.refreshToken) {
      const refreshTokenString = typeof responseData.refreshToken === 'string'
        ? responseData.refreshToken
        : responseData.refreshToken.token;
      saveTokens(responseData.token, refreshTokenString);
    }
    
    return responseData;
  },

  /**
   * Login with email and password
   * POST /users/login
   * clientContext defaults to the admin portal (backend rejects non-admins);
   * member flows (e.g. Song Studio /upload) pass 'member_portal' so any
   * active church member can sign in.
   */
  login: async (data: LoginRequest, clientContext: string = 'admin_portal'): Promise<LoginResponse> => {
    const loginData = { ...data, clientContext };
    const response = await apiClient.post('/users/login', loginData);
    
    // Save tokens from response body
    const responseData = response.data.data || response.data;
    if (responseData.token && responseData.refreshToken) {
      const refreshTokenString = typeof responseData.refreshToken === 'string' 
        ? responseData.refreshToken 
        : responseData.refreshToken.token;
      saveTokens(responseData.token, refreshTokenString);
    }
    
    return responseData;
  },

  /**
   * Register new user
   * POST /users/register
   * Returns sessionToken for verification
   */
  register: async (data: RegisterRequest): Promise<{ data: { sessionToken: string; message: string } }> => {
    const response = await apiClient.post('/users/register', data);
    return response.data;
  },

  /**
   * Logout current user
   * POST /users/logout
   */
  logout: async (): Promise<void> => {
    await apiClient.post('/users/logout');
    clearTokens();
  },

  /**
   * Verify the sign-up email with the 6-digit code
   * POST /verify-onboarding
   */
  verifyOnboarding: async (code: string, sessionToken: string): Promise<{ onboardingToken: string; userId: string; email: string; message: string }> => {
    const response = await apiClient.post('/verify-onboarding', { code, sessionToken });
    // Backend returns 'token' but we rename it to 'onboardingToken' for clarity
    const data = response.data;
    return {
      onboardingToken: data.token,
      userId: data.userId,
      email: data.email,
      message: data.message,
    };
  },

  /**
   * Resend verification code
   * POST /resend-verification
   */
  resendVerification: async (data: ResendVerificationRequest | string): Promise<{ sessionToken: string; message: string }> => {
    const payload = typeof data === 'string' ? { sessionToken: data } : data;
    const response = await apiClient.post('/resend-verification', payload);
    return response.data;
  },

  /**
   * Finalize admin setup after church creation
   * POST /finalize-admin-setup
   */
  finalizeAdminSetup: async (churchId: string, onboardingToken: string): Promise<LoginResponse> => {
    const response = await apiClient.post(
      '/finalize-admin-setup',
      { churchId },
      {
        headers: { Authorization: `Bearer ${onboardingToken}` },
      }
    );
    return response.data;
  },

  /**
   * Get current user profile
   * GET /users/profile
   */
  getProfile: async (): Promise<User> => {
    const response = await apiClient.get('/users/profile');
    return response.data.data || response.data;
  },

  /**
   * Update name and email (email changes need the current password)
   * PUT /users/profile
   */
  updateUserProfile: async (updates: { username?: string; email?: string }, currentPassword?: string): Promise<User> => {
    const payload = currentPassword ? { ...updates, currentPassword } : updates;
    const response = await apiClient.put('/users/profile', payload);
    return response.data.data || response.data;
  },

  /**
   * Set or change password
   * POST /users/set-password
   */
  setPassword: async (currentPassword: string | null, newPassword: string): Promise<void> => {
    await apiClient.post('/users/set-password', {
      currentPassword,
      newPassword,
    });
  },

  /**
   * Batch get users by IDs
   * POST /users/batch
   */
  batchGetUsers: async (userIds: string[]): Promise<User[]> => {
    if (!userIds || userIds.length === 0) return [];
    try {
      const response = await apiClient.post('/users/batch', { userIds });
      return response.data;
    } catch (error) {
      console.error('Batch user lookup failed:', error);
      return [];
    }
  },

  /**
   * Update user church assignment
   * PUT /users/updateUserChurch
   */
  updateUserChurch: async (userId: string, churchId: string, userRole: string): Promise<User> => {
    const response = await apiClient.put('/users/updateUserChurch', {
      id: userId,
      churchId,
      userRole,
    });
    return response.data.data || response.data;
  },

  /**
   * Request password reset code
   * POST /users/forgot-password
   */
  forgotPassword: async (email: string): Promise<{ message: string; status?: string; externalProvider?: string }> => {
    const response = await apiClient.post('/users/forgot-password', { email });
    return response.data;
  },

  /**
   * Verify password reset code
   * POST /users/verify-reset-code
   */
  verifyResetCode: async (email: string, code: string): Promise<{ resetToken: string; message: string }> => {
    const response = await apiClient.post('/users/verify-reset-code', { email, code });
    return response.data;
  },

  /**
   * Reset password with reset token
   * POST /users/reset-password
   */
  resetPassword: async (resetToken: string, newPassword: string): Promise<{ message: string }> => {
    const response = await apiClient.post('/users/reset-password', { resetToken, newPassword });
    return response.data;
  },

  /**
   * Switch the caller's active church. Server validates the caller has an active
   * membership in the target church, updates DefaultChurchId, and mints a fresh
   * JWT with the target church's role. New tokens are saved to localStorage so
   * subsequent API calls carry the correct role/churchId claims.
   *
   * POST /users/switch-church
   */
  switchChurch: async (churchId: string): Promise<{ token: string; refreshToken: string }> => {
    const response = await apiClient.post('/users/switch-church', { churchId });
    const data = response.data.data || response.data;
    if (data?.token && data?.refreshToken) {
      const refreshTokenString = typeof data.refreshToken === 'string'
        ? data.refreshToken
        : data.refreshToken.token;
      saveTokens(data.token, refreshTokenString);
    }
    return { token: data.token, refreshToken: data.refreshToken };
  },

};

export default authApi;

